import { useEffect, useState } from "react";
import { Alert, Button, Form } from "react-bootstrap";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import styled from "@emotion/styled";
import * as yup from "yup";
import { requestPublicGitHubRepoAnalysis } from "@/api/requests/code-repo/requests";
import { trackUserInteraction } from "@/api/requests/user-interaction/requests";
import { InteractionTypeDto } from "@/api/generated/models";
import { useYupResolver } from "@/hooks/useYupResolver";
import { useYupSchema } from "@/hooks/useYupSchema";
import Input from "../form/Input";

type FormValues = {
  githubUrl?: string;
};

const messages = defineMessages({
  urlPlaceholder: {
    id: "GitHubRepoAnalysisForm.urlPlaceholder",
    defaultMessage: "https://github.com/hack-stalker/hack-stalker-demo",
  },
  apiError: {
    id: "GitHubRepoAnalysisForm.apiError",
    defaultMessage: "An error occurred while processing your request.",
  },
  invalidUrlError: {
    id: "GitHubRepoAnalysisForm.invalidUrlError",
    defaultMessage:
      "Invalid URL. Please check the format and try again. Example: https://github.com/hack-stalker/hack-stalker-demo",
  },
});

const githubUrlRegex = new RegExp(
  /((https:\/\/)?github\.com\/([a-zA-Z0-9._-]+)\/([a-zA-Z0-9._-]+)\/?|^\s*$)$/,
);

const StyledForm = styled(Form)`
  position: relative;
  width: 100%;
`;

const StyledAlert = styled(Alert)`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;

  margin-top: 0.5rem;

  z-index: calc(var(--zindex-tooltip) + 1);
`;

const SearchbarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  gap: 1rem;
`;

const GitHubRepoAnalysisForm = ({
  initialGitHubUrl,
  testId,
}: {
  initialGitHubUrl?: string;
  testId?: string;
}) => {
  const intl = useIntl();
  const [apiError, setApiError] = useState("");

  const schema = useYupSchema((intl) => ({
    githubUrl: yup.string().matches(githubUrlRegex, intl.formatMessage(messages.invalidUrlError)),
  }));
  const resolver = useYupResolver(schema);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors, dirtyFields },
  } = useForm<FormValues>({
    resolver,
    defaultValues: {
      githubUrl: initialGitHubUrl ?? "",
    },
  });

  const router = useRouter();

  const onSubmit = handleSubmit(async ({ githubUrl }, event) => {
    event?.preventDefault();
    setApiError("");

    if (!githubUrl || githubUrl.trim().length === 0) {
      githubUrl = "https://github.com/hack-stalker/hack-stalker-demo";
    }

    // we only get here if the regex validation succeeded
    const [_, __, ___, account, repo] = githubUrlRegex.exec(githubUrl)!;

    try {
      const result = await requestPublicGitHubRepoAnalysis(account, repo);
      await router.push(`/github.com/${account}/${repo}/analysis/${result.id}`);
    } catch (error) {
      console.error(error);
      setApiError(intl.formatMessage(messages.apiError));
    }

    try {
      await trackUserInteraction({
        type: InteractionTypeDto.ScanRepository,
        repositoryUrl: githubUrl,
      });
    } catch (error) {
      console.error(error);
      setApiError(intl.formatMessage(messages.apiError));
    }
  });

  useEffect(() => {
    // if the initialGitHubUrl is set and the githubUrl field is not dirty, reset the form
    if (initialGitHubUrl && !dirtyFields.githubUrl) {
      reset({ githubUrl: initialGitHubUrl });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialGitHubUrl]);

  return (
    <StyledForm validated={isValid} onSubmit={onSubmit}>
      <SearchbarWrapper>
        <Input
          name="githubUrl"
          placeholder={messages.urlPlaceholder}
          register={register}
          errors={errors}
          data-testid={testId}
          prefix={<FontAwesomeIcon icon={faSearch} />}
          noMargin
          absoluteFeedback
          className="font-monospace"
          flexGrow
        />
        <Button variant="outline-primary" onClick={onSubmit}>
          <FormattedMessage id="Header.tryNow" defaultMessage="Try Now" />
        </Button>
      </SearchbarWrapper>

      {apiError && <StyledAlert variant="danger">{apiError}</StyledAlert>}
    </StyledForm>
  );
};

export default GitHubRepoAnalysisForm;
